// Plan 23a Task 5.1 — Tab Bar primitive (horizontal tabs with sliding amber underline).
// Behavior contract: docs/design/components.md §7 lines 254–274.
//
// F20a-CLASS-1 compliance: underline slide uses tl.to(underline, { x }, 0)
// with explicit position 0 (one-shot parallel tween, NOT chained-append).

import gsap from 'gsap';
import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface TabBarOpts {
  /** Tab definitions — must contain at least 1 entry. */
  tabs: { label: string; id: string }[];
  /** Index of initially active tab. Default: 0. */
  activeIndex?: number;
  /** Called only on click interactions, NOT on programmatic setActive(). */
  onChange?: (index: number, id: string) => void;
}

export interface TabBarHandle extends UIComponentHandle {
  /** Programmatically activate a tab by index. Does NOT fire onChange. */
  setActive(index: number): void;
  /** Currently active tab index. */
  readonly activeIndex: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** animation.duration.fast = 150ms → seconds for GSAP */
const SLIDE_DURATION_S = 0.15;

/** animation.easing.snappy */
const SNAPPY_EASE = 'cubic-bezier(0.2, 0, 0, 1)';

/** color.brand.amber — underline color */
const AMBER = '#E8A020';

/** color.text.primary — active tab label */
const TEXT_PRIMARY = '#FFFFFF';

/** color.text.muted — inactive tab label */
const TEXT_MUTED = '#707888';

/** border.divider — container bottom border */
const BORDER_DIVIDER = '1px solid rgba(255, 255, 255, 0.10)';

/** typography.family.headline */
const FONT_HEADLINE = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.size.h5 = 18px */
const FONT_SIZE_H5 = '18px';

/** typography.weight.bold = 700 */
const FONT_WEIGHT_BOLD = '700';

/** spacing.md = 16px (horizontal padding on tabs) */
const SPACING_MD = '16px';

/** spacing.sm = 8px (vertical padding on tabs) */
const SPACING_SM = '8px';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createTabBar(config: UIComponentConfig & TabBarOpts): TabBarHandle {
  const { gsapRegistry, mount, tabs, activeIndex: initialIndex = 0, onChange } = config;

  let _activeIndex = Math.max(0, Math.min(initialIndex, tabs.length - 1));
  let disposed = false;
  let currentTl: GsapTimelineHandle | null = null;

  // ── Build DOM ──────────────────────────────────────────────────────────────

  // Container: full-width, position:relative (needed for absolute underline),
  // bottom border = border.divider
  const el = document.createElement('div');
  el.className = 'ui-tab-bar';
  el.setAttribute('role', 'tablist');
  el.style.cssText = [
    'display: flex',
    'flex-direction: row',
    'align-items: flex-end',
    'position: relative',
    'width: 100%',
    `border-bottom: ${BORDER_DIVIDER}`,
    'box-sizing: border-box',
  ].join('; ');

  // Build tab elements
  const tabEls: HTMLButtonElement[] = tabs.map((tab, idx) => {
    const tabEl = document.createElement('button');
    tabEl.type = 'button';
    tabEl.className = 'ui-tab-bar__tab';
    tabEl.setAttribute('role', 'tab');
    tabEl.setAttribute('aria-selected', idx === _activeIndex ? 'true' : 'false');
    tabEl.setAttribute('data-tab-id', tab.id);
    tabEl.style.cssText = [
      'display: inline-flex',
      'align-items: center',
      'justify-content: center',
      `padding: ${SPACING_SM} ${SPACING_MD}`,
      'min-height: 48px',
      'border: 0',
      'background: transparent',
      `font-family: ${FONT_HEADLINE}`,
      `font-size: ${FONT_SIZE_H5}`,
      `font-weight: ${FONT_WEIGHT_BOLD}`,
      'text-transform: uppercase',
      'letter-spacing: 0',
      'cursor: pointer',
      'user-select: none',
      'position: relative',
      'white-space: nowrap',
      'touch-action: manipulation',
      '-webkit-tap-highlight-color: transparent',
      // Color set below via applyColors()
    ].join('; ');

    tabEl.textContent = tab.label.toUpperCase();

    // Click handler — only fires onChange, NOT triggered by setActive()
    const handleClick = () => {
      if (disposed) return;
      activateTab(idx, /* fireOnChange */ true);
    };

    tabEl.addEventListener('click', handleClick);
    // Store cleanup ref on the element for dispose()
    (tabEl as HTMLElement & { _spClickHandler?: () => void })._spClickHandler = handleClick;

    el.appendChild(tabEl);
    return tabEl;
  });

  // Underline indicator: 3px amber bar, absolutely positioned at bottom of container,
  // slides horizontally via GSAP
  const underline = document.createElement('div');
  underline.className = 'ui-tab-bar__underline';
  underline.style.cssText = [
    'position: absolute',
    'bottom: 0',
    'left: 0',
    'height: 3px',
    `background-color: ${AMBER}`,
    // Width will be set on first activateTab call
    'width: 0',
    // Pointer-events none so it doesn't intercept clicks
    'pointer-events: none',
  ].join('; ');

  el.appendChild(underline);

  // ── Helpers ────────────────────────────────────────────────────────────────

  function killCurrent(): void {
    if (currentTl) {
      currentTl.dispose();
      currentTl = null;
    }
  }

  /** Apply active/inactive text colors to all tabs. */
  function applyColors(activeIdx: number): void {
    tabEls.forEach((tabEl, i) => {
      tabEl.style.color = i === activeIdx ? TEXT_PRIMARY : TEXT_MUTED;
    });
  }

  /** Apply is-active class to the active tab, remove from all others. */
  function applyActiveClass(activeIdx: number): void {
    tabEls.forEach((tabEl, i) => {
      if (i === activeIdx) {
        tabEl.classList.add('is-active');
        tabEl.setAttribute('aria-selected', 'true');
      } else {
        tabEl.classList.remove('is-active');
        tabEl.setAttribute('aria-selected', 'false');
      }
    });
  }

  /**
   * Slide the underline to the target tab position.
   *
   * F20a-CLASS-1: ALWAYS uses tl.to(underline, { x: targetX }, 0) with explicit
   * position argument 0. This is a one-shot parallel tween — not chained-append.
   */
  function slideUnderline(targetIdx: number): void {
    if (disposed) return;

    const targetEl = tabEls[targetIdx];
    if (!targetEl) return;

    // Read layout values from DOM (offsetLeft / offsetWidth)
    const targetX = targetEl.offsetLeft;
    const targetW = targetEl.offsetWidth;

    // Update underline width to match target tab
    underline.style.width = `${targetW}px`;

    // Kill any in-flight slide
    killCurrent();

    // Create new timeline — F20a-CLASS-1 pattern
    const handle = gsapRegistry.create('tab-bar:underline-slide');
    const tl = handle.timeline;

    tl.to(
      underline,
      {
        x: targetX,
        duration: SLIDE_DURATION_S,
        ease: SNAPPY_EASE,
      },
      0, // explicit position — F20a-CLASS-1 sentinel
    );

    currentTl = handle;
  }

  /** Core activation logic. fireOnChange=true only for click interactions. */
  function activateTab(idx: number, fireOnChange: boolean): void {
    if (disposed) return;
    const clamped = Math.max(0, Math.min(idx, tabs.length - 1));
    _activeIndex = clamped;

    applyColors(clamped);
    applyActiveClass(clamped);
    slideUnderline(clamped);

    if (fireOnChange) {
      onChange?.(clamped, tabs[clamped].id);
    }
  }

  // ── Initial state ──────────────────────────────────────────────────────────

  // Apply initial colors + active class
  applyColors(_activeIndex);
  applyActiveClass(_activeIndex);

  // Set initial underline position via gsap.set() so GSAP's cache is correct
  // for the first animated slide (avoids NaN translation on first tl.to()).
  // We also initialise width here; offsetLeft is 0 at construction in happy-dom.
  const initEl = tabEls[_activeIndex];
  if (initEl) {
    const initX = initEl.offsetLeft;
    const initW = initEl.offsetWidth;
    underline.style.width = `${initW}px`;
    gsap.set(underline, { x: initX });
  }

  // ── Optional mount at construction time ───────────────────────────────────

  if (mount && !mount.contains(el)) {
    mount.appendChild(el);
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    killCurrent();

    // Remove click handlers from all tab elements
    tabEls.forEach((tabEl) => {
      const el = tabEl as HTMLElement & { _spClickHandler?: () => void };
      if (el._spClickHandler) {
        el.removeEventListener('click', el._spClickHandler);
        delete el._spClickHandler;
      }
    });

    // Detach from DOM if mounted
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }

  return {
    element: el,
    get activeIndex(): number {
      return _activeIndex;
    },
    setActive(index: number): void {
      activateTab(index, /* fireOnChange */ false);
    },
    dispose,
  };
}
